/**
 * VerificationActions Component
 *
 * Action buttons for lab result verification flow.
 * Allows nurse to choose: assign to doctor OR skip review (routine result).
 */
import { useState, useEffect } from 'react'
import {
  Card,
  Radio,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  message,
  Spin,
} from 'antd'
import {
  CheckCircleOutlined,
  UserSwitchOutlined,
  FastForwardOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import { BiomarkerValue, biomarkersToApiFormat } from '../lib/biomarker-config'

const { TextArea } = Input
const { Text, Title } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

type VerificationAction = 'assign-doctor' | 'skip-review'

interface Doctor {
  id: string
  name: string
}

interface VerificationActionsProps {
  labResultId: string
  biomarkers: BiomarkerValue[]
  onSuccess: () => void
  disabled?: boolean
  currentStatus?: string
}

/**
 * Fetch doctors list from API
 * Uses /admin/lab-results/doctors which returns Doctor table IDs (not User IDs)
 */
async function fetchDoctors(token: string): Promise<Doctor[]> {
  const response = await fetch(`${API_URL}/admin/lab-results/doctors`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch doctors')
  }

  const data = await response.json()
  return data || []
}

/**
 * Update lab result with biomarkers
 */
async function updateLabResult(
  token: string,
  labResultId: string,
  biomarkers: BiomarkerValue[],
  notes?: string,
): Promise<void> {
  const results = biomarkersToApiFormat(biomarkers)

  const response = await fetch(`${API_URL}/admin/lab-results/${labResultId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      results,
      verificationNotes: notes,
    }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to update lab result')
  }
}

/**
 * Verify lab result
 */
async function verifyLabResult(
  token: string,
  labResultId: string,
): Promise<void> {
  const response = await fetch(`${API_URL}/admin/lab-results/${labResultId}/verify`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to verify lab result')
  }
}

/**
 * Assign lab result to doctor
 */
async function assignToDoctor(
  token: string,
  labResultId: string,
  doctorId: string,
): Promise<void> {
  const response = await fetch(`${API_URL}/admin/lab-results/${labResultId}/assign-doctor`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ doctorId }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to assign doctor')
  }
}

/**
 * Skip review (approve as routine)
 */
async function skipReview(
  token: string,
  labResultId: string,
): Promise<void> {
  const response = await fetch(`${API_URL}/admin/lab-results/${labResultId}/approve`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to approve lab result')
  }
}

/**
 * Main VerificationActions Component
 */
export function VerificationActions({
  labResultId,
  biomarkers,
  onSuccess,
  disabled = false,
  currentStatus,
}: VerificationActionsProps) {
  const [action, setAction] = useState<VerificationAction>('assign-doctor')
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [loadingDoctors, setLoadingDoctors] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  // Load doctors on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_access_token')
    if (!token) return

    setLoadingDoctors(true)
    fetchDoctors(token)
      .then((data) => {
        setDoctors(data)
        // Pre-select first doctor if available
        if (data.length > 0) {
          setSelectedDoctorId(data[0].id)
        }
      })
      .catch((err) => {
        console.error('Failed to load doctors:', err)
        message.error('Greška pri učitavanju lekara')
      })
      .finally(() => {
        setLoadingDoctors(false)
      })
  }, [])

  const getDoctorName = (doctor: Doctor) => {
    return doctor.name
  }

  const filledBiomarkers = biomarkers.filter((b) => b.value !== null)
  const hasBiomarkers = filledBiomarkers.length > 0

  const canSubmit =
    !disabled &&
    hasBiomarkers &&
    (action === 'skip-review' || (action === 'assign-doctor' && selectedDoctorId))

  const handleSubmit = async () => {
    const token = localStorage.getItem('admin_access_token')
    if (!token) {
      message.error('Niste prijavljeni')
      return
    }

    setSubmitting(true)

    try {
      // Step 1: Update lab result with biomarkers
      await updateLabResult(token, labResultId, biomarkers, notes)

      // Step 2: Verify lab result (only if not already verified)
      const needsVerification = currentStatus === 'PENDING' || currentStatus === 'RECEIVED'
      if (needsVerification) {
        await verifyLabResult(token, labResultId)
      }

      // Step 3: Either assign to doctor or approve
      if (action === 'assign-doctor' && selectedDoctorId) {
        await assignToDoctor(token, labResultId, selectedDoctorId)
        message.success('Rezultat verifikovan i dodeljen lekaru')
      } else {
        await skipReview(token, labResultId)
        message.success('Rezultat verifikovan i odobren')
      }

      onSuccess()
    } catch (error) {
      console.error('Verification failed:', error)
      message.error(
        `Greška: ${error instanceof Error ? error.message : 'Neuspela verifikacija'}`,
      )
    } finally {
      setSubmitting(false)
    }
  }

  // Check if already approved or shared
  const isApproved = currentStatus === 'APPROVED' || currentStatus === 'SHARED_WITH_PATIENT'
  const isAwaitingReview = currentStatus === 'AWAITING_REVIEW'

  if (isApproved) {
    return (
      <Card>
        <Alert
          message="Rezultat je odobren"
          description="Ovaj laboratorijski rezultat je već verifikovan i odobren."
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
        />
      </Card>
    )
  }

  if (isAwaitingReview) {
    return (
      <Card>
        <Alert
          message="Čeka pregled lekara"
          description="Ovaj rezultat je dodeljen lekaru na pregled."
          type="info"
          showIcon
        />
      </Card>
    )
  }

  return (
    <Card
      title={
        <Space>
          <CheckCircleOutlined />
          <span>Verifikacija</span>
        </Space>
      }
    >
      {!hasBiomarkers && (
        <Alert
          message="Unesite biomarkere"
          description="Potrebno je uneti bar jednu vrednost biomarkera pre verifikacije."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          Beleške
        </Title>
        <TextArea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Dodajte beleške o rezultatima (opciono)..."
          rows={3}
          disabled={disabled || submitting}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          Akcija nakon verifikacije
        </Title>
        <Radio.Group
          value={action}
          onChange={(e) => setAction(e.target.value)}
          disabled={disabled || submitting}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio value="assign-doctor" style={{ width: '100%' }}>
              <Space>
                <UserSwitchOutlined />
                <span>Dodeli lekaru na pregled</span>
              </Space>
            </Radio>
            {action === 'assign-doctor' && (
              <div style={{ marginLeft: 24, marginTop: 8 }}>
                {loadingDoctors ? (
                  <Spin size="small" />
                ) : (
                  <Select
                    value={selectedDoctorId}
                    onChange={setSelectedDoctorId}
                    placeholder="Izaberi lekara"
                    style={{ width: 250 }}
                    disabled={disabled || submitting}
                  >
                    {doctors.map((doctor) => (
                      <Select.Option key={doctor.id} value={doctor.id}>
                        {getDoctorName(doctor)}
                      </Select.Option>
                    ))}
                  </Select>
                )}
                {doctors.length === 0 && !loadingDoctors && (
                  <Text type="secondary">Nema dostupnih lekara</Text>
                )}
              </div>
            )}
            <Radio value="skip-review">
              <Space>
                <FastForwardOutlined />
                <span>Rutinski rezultat (preskoči pregled lekara)</span>
              </Space>
            </Radio>
          </Space>
        </Radio.Group>
      </div>

      <Button
        type="primary"
        size="large"
        icon={<SaveOutlined />}
        onClick={handleSubmit}
        loading={submitting}
        disabled={!canSubmit}
        block
      >
        {action === 'assign-doctor' ? 'Verifikuj i dodeli lekaru' : 'Verifikuj i odobri'}
      </Button>

      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Biće sačuvano {filledBiomarkers.length} biomarker(a)
        </Text>
      </div>
    </Card>
  )
}

export default VerificationActions
